import React, { useEffect, useMemo, useState } from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { LanguageThemeSelector } from '../common';
import { SupportChatList } from '../Support';
import api from '../../services/api';
import Profile from '../Dashboard/Profile';
import ChangePassword from '../Dashboard/ChangePassword';
import AiAssistantTopic from './topics/AiAndSupportTopics';
import ChatAppStoresSection from './topics/ProfileTopic';
import {
  DeductionsReport,
  ReturnsSummaryReport,
  SalesReport,
  TopupsReport,
} from './topics/ReportsTopics';
import './ChatApp.css';

const TOPICS = {
  support: 'support',
  ai: 'ai',
  reportDeductions: 'reportDeductions',
  reportTopups: 'reportTopups',
  reportSales: 'reportSales',
  reportReturns: 'reportReturns',
  profile: 'profile',
  security: 'security',
};

export default function ChatAppShell({ onSwitchClassic, stores: externalStores }) {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [activeTopic, setActiveTopic] = useState(TOPICS.support);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [stores, setStores] = useState(externalStores || []);

  useEffect(() => {
    if (externalStores?.length) {
      setStores(externalStores);
      return;
    }
    api.getStores().then((r) => setStores(r || [])).catch(() => {});
  }, [externalStores]);

  const storeExternalId = useMemo(() => {
    if (!selectedStoreId) return null;
    const s = stores.find((x) => x.id === selectedStoreId);
    return s?.store_id || null;
  }, [selectedStoreId, stores]);

  const topicTitle = {
    [TOPICS.support]: t('chatApp.topicSupport'),
    [TOPICS.ai]: t('chatApp.topicAi'),
    [TOPICS.reportDeductions]: t('chatApp.reportDeductions'),
    [TOPICS.reportTopups]: t('chatApp.reportTopups'),
    [TOPICS.reportSales]: t('chatApp.reportSales'),
    [TOPICS.reportReturns]: t('chatApp.reportReturns'),
    [TOPICS.profile]: t('chatApp.topicProfile'),
    [TOPICS.security]: t('security.title'),
  }[activeTopic];

  const renderBody = () => {
    switch (activeTopic) {
      case TOPICS.support:
        return (
          <SupportChatList
            isAdmin={false}
            onUnreadCountChange={() => {}}
            onNotificationAdd={() => {}}
            onSelectedChatChange={() => {}}
          />
        );
      case TOPICS.ai:
        return <AiAssistantTopic storeId={storeExternalId} />;
      case TOPICS.reportDeductions:
        return <DeductionsReport storeId={storeExternalId} />;
      case TOPICS.reportTopups:
        return <TopupsReport storeId={storeExternalId} />;
      case TOPICS.reportSales:
        return <SalesReport selectedStoreId={storeExternalId} />;
      case TOPICS.reportReturns:
        return <ReturnsSummaryReport storeId={storeExternalId} />;
      case TOPICS.profile:
        return (
          <>
            <Profile />
            <ChatAppStoresSection />
          </>
        );
      case TOPICS.security:
        return <ChangePassword />;
      default:
        return null;
    }
  };

  const TopicBtn = ({ id, avatar, label, sub }) => (
    <button
      type="button"
      className={`chat-app-topic ${activeTopic === id ? 'active' : ''}`}
      onClick={() => setActiveTopic(id)}
    >
      <span className={`chat-app-topic-avatar ${id === TOPICS.ai ? 'cat' : ''}`}>{avatar}</span>
      <span>
        <div className="chat-app-topic-label">{label}</div>
        {sub && <div className="chat-app-topic-sub">{sub}</div>}
      </span>
    </button>
  );

  return (
    <div className="chat-app">
      <aside className="chat-app-sidebar">
        <div className="chat-app-sidebar-header">
          <h1>Indigo Warehouse</h1>
          <small className="opacity-75">{user?.email}</small>
        </div>
        <div className="chat-app-topics">
          <TopicBtn id={TOPICS.support} avatar="🎧" label={t('chatApp.topicSupport')} sub={t('chatApp.topicSupportSub')} />
          <TopicBtn id={TOPICS.ai} avatar="🐱" label={t('chatApp.topicAi')} sub={t('chatApp.topicAiSub')} />
          <div className="px-2 py-1 small opacity-75 text-uppercase">{t('chatApp.reports')}</div>
          <TopicBtn id={TOPICS.reportDeductions} avatar="📉" label={t('chatApp.reportDeductions')} />
          <TopicBtn id={TOPICS.reportTopups} avatar="📈" label={t('chatApp.reportTopups')} />
          <TopicBtn id={TOPICS.reportSales} avatar="🛒" label={t('chatApp.reportSales')} />
          <TopicBtn id={TOPICS.reportReturns} avatar="↩️" label={t('chatApp.reportReturns')} />
          <div className="px-2 py-1 small opacity-75 text-uppercase mt-2">{t('chatApp.settings')}</div>
          <TopicBtn id={TOPICS.profile} avatar="👤" label={t('chatApp.topicProfile')} />
          <TopicBtn id={TOPICS.security} avatar="🔒" label={t('security.title')} />
        </div>
        <div className="p-2 border-top border-light border-opacity-25">
          <Button
            variant="light"
            size="sm"
            className="w-100 mb-2"
            onClick={() => {
              if (onSwitchClassic) onSwitchClassic();
            }}
          >
            {t('chatApp.switchClassic')}
          </Button>
          <Button variant="outline-light" size="sm" className="w-100" onClick={logout}>
            {t('nav.logout')}
          </Button>
        </div>
      </aside>

      <main className="chat-app-main">
        <header className="chat-app-main-header">
          <div>
            <strong>{topicTitle}</strong>
          </div>
          <div className="d-flex align-items-center gap-2">
            {stores.length > 0 && (
              <Dropdown>
                <Dropdown.Toggle variant="outline-secondary" size="sm">
                  {selectedStoreId
                    ? stores.find((s) => s.id === selectedStoreId)?.name || t('nav.allStores')
                    : t('nav.allStores')}
                </Dropdown.Toggle>
                <Dropdown.Menu align="end">
                  <Dropdown.Item active={!selectedStoreId} onClick={() => setSelectedStoreId(null)}>
                    {t('nav.allStores')}
                  </Dropdown.Item>
                  {stores.map((s) => (
                    <Dropdown.Item
                      key={s.id}
                      active={selectedStoreId === s.id}
                      onClick={() => setSelectedStoreId(s.id)}
                    >
                      {s.name || s.store_id}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            )}
            <LanguageThemeSelector />
          </div>
        </header>
        <div className="chat-app-main-body">{renderBody()}</div>
      </main>
    </div>
  );
}
